import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type Address from '../../types/Address';
import { Redeem, RedeemFlowResponse } from '../../types/Redeem';
import { RootState } from '../store';

/**
 * Slice-internal state shape. Distinct from `Redeem` because the slice
 * has an "empty" initial state (no code captured yet) where `flow`
 * isn't known. `Redeem` requires `flow` because anything holding one
 * needs a flow to render — that invariant is enforced at the boundary
 * where consumers read out a `Redeem` (see `useRedeemAuth`), not inside
 * the slice itself.
 */
export interface RedeemSliceState {
  code: string;
  customerId: string;
  flow?: RedeemFlowResponse;
  shippingCountries?: string[];
  shippingAddress?: Address;
}

const initialState: RedeemSliceState = {
  code: '',
  customerId: '',
  // `flow`, `shippingCountries`, `shippingAddress` are intentionally
  // omitted: they're only populated once `validateRedeemCode` resolves
  // and the user moves through the flow.
};

/* Reducers */
const redeemSlice = createSlice({
  name: 'redeem',
  initialState,
  reducers: {
    /**
     * Stores the redeem auth payload after the validate-redeem-code call
     * resolves. Code validity is enforced server-side at submit time, so
     * we don't track a client-side freshness window — if the code has
     * expired between validation and submission, the API will return the
     * appropriate failure reason and the user will be redirected then.
     */
    authenticated(state, action: PayloadAction<Redeem>) {
      state.code = action.payload.code;
      state.customerId = action.payload.customerId;
      // `flow` is required on the payload (`Redeem`), so always assign.
      state.flow = action.payload.flow;
      // `shippingCountries` and `shippingAddress` remain optional — only
      // assign when the payload supplies them so we don't overwrite with
      // `undefined` and lose previously-stored data on a no-op re-dispatch.
      if (action.payload.shippingCountries) {
        state.shippingCountries = action.payload.shippingCountries;
      }
      if (action.payload.shippingAddress) {
        state.shippingAddress = action.payload.shippingAddress;
      }
    },
    /**
     * Captures the address the user confirmed on the address-collection
     * step (after the verify-address modal closes). The summary page
     * reads this back to render the order summary.
     */
    shippingAddressUpdated(state, action: PayloadAction<Address>) {
      state.shippingAddress = action.payload;
    },
    /**
     * Resets the slice to its initial empty state. Dispatch on success,
     * an unrecoverable failure, or an explicit "start over" so we don't
     * leave stale auth around.
     */
    cleared() {
      return initialState;
    },
  },
});

/* Actions */
export const { authenticated, cleared, shippingAddressUpdated } =
  redeemSlice.actions;

/* Selectors */
export const selectRedeem = (state: RootState) => state.redeem;

/** True when a code has been captured. */
export const selectIsRedeemAuthenticated = (state: RootState) =>
  Boolean(state.redeem.code);

export default redeemSlice.reducer;
